import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { shouldSkipExtraLookupConfirm } from '../components/vehicles/ExtraLookupConfirmDialog';
import { isFinnishRegistrationNumber } from '../utils/registration';

/**
 * Vehicle registration lookup flow for work-order creation dialogs.
 *
 * Encapsulates the lookup quota, the free org-DB search (by-registration),
 * the debounced vehicle typeahead, the paid external lookup with its
 * 402 handling (trial restriction / over-quota extra-lookup confirmation),
 * and interrupt/cancel/reset plumbing.
 *
 * Semantics are the dashboard dialog's (AddMaintenanceDialog): an org-DB hit
 * short-circuits and never spends a paid lookup, and selecting a typeahead
 * option only does the free by-registration fetch.
 *
 * The caller owns the registration input value and the vehicle form state:
 * - onVehicleData(vehicleData, regNumber): merge found/looked-up data into the
 *   form and update `vehicleLookedUp` (via the returned setter) as needed.
 * - onLastCustomer(customer): the vehicle's last customer from the org DB;
 *   the caller decides whether to auto-select it.
 * - onTrialRestricted(): lookup denied on trial; caller may e.g. switch the
 *   form to manual vehicle entry.
 */
export default function useVehicleLookup({ t, onVehicleData, onLastCustomer, onTrialRestricted }) {
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState(null);
  const [vehicleLookedUp, setVehicleLookedUp] = useState(false);
  const [showTrialInfo, setShowTrialInfo] = useState(false);
  const [showNonFinnishInfo, setShowNonFinnishInfo] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [extraLookupDialogOpen, setExtraLookupDialogOpen] = useState(false);
  const [pendingRegNumber, setPendingRegNumber] = useState(null);
  const [vehicleTypeaheadOptions, setVehicleTypeaheadOptions] = useState([]);
  const [vehicleTypeaheadLoading, setVehicleTypeaheadLoading] = useState(false);

  const lookupAbortControllerRef = useRef(null);
  const autoSearchTimerRef = useRef(null);
  const vehicleTypeaheadTimerRef = useRef(null);
  const vehicleTypeaheadOpenRef = useRef(false);

  // Callbacks are read through a ref at call time so handler identities stay
  // stable even when the caller passes inline functions.
  const callbacksRef = useRef({});
  callbacksRef.current = { onVehicleData, onLastCustomer, onTrialRestricted };

  useEffect(() => {
    if (vehicleLookedUp) {
      setVehicleTypeaheadOptions([]);
    }
  }, [vehicleLookedUp]);

  // Fetch quota info
  const fetchQuota = useCallback(async () => {
    try {
      const response = await axios.get('/api/vehicles/lookup-quota/');
      setQuotaInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch lookup quota:', error);
    }
  }, []);

  // Search for existing vehicle in organization database (free)
  const searchExistingVehicle = useCallback(async (regNumber) => {
    if (!regNumber || regNumber.length < 3) {
      return null;
    }

    try {
      const response = await axios.get(`/api/vehicles/by-registration/?registration_number=${encodeURIComponent(regNumber)}`, {
        signal: lookupAbortControllerRef.current?.signal,
      });

      if (response.data.found && response.data.vehicle) {
        return { vehicle: response.data.vehicle, lastCustomer: response.data.last_customer || null };
      }
      return null;
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        return null;
      }
      console.error('Vehicle search failed:', error);
      return null;
    }
  }, []);

  const searchVehicleTypeahead = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setVehicleTypeaheadOptions([]);
      return;
    }
    setVehicleTypeaheadLoading(true);
    try {
      const response = await axios.get(`/api/vehicles/typeahead/?search=${encodeURIComponent(query)}`);
      setVehicleTypeaheadOptions(response.data || []);
    } catch (err) {
      setVehicleTypeaheadOptions([]);
    } finally {
      setVehicleTypeaheadLoading(false);
    }
  }, []);

  const applyFoundVehicle = useCallback((result, regNumber) => {
    callbacksRef.current.onVehicleData?.(result.vehicle, regNumber);
    if (result.lastCustomer) {
      callbacksRef.current.onLastCustomer?.(result.lastCustomer);
    }
  }, []);

  /**
   * Registration input changed (caller passes the cleaned, uppercased value).
   * Resets lookup state and schedules the debounced typeahead + free org-DB
   * auto-search.
   */
  const handleRegistrationInput = useCallback((value) => {
    setVehicleLookedUp(false);
    setLookupError(null);
    setShowNonFinnishInfo(false);
    setShowTrialInfo(false);

    // Cancel previous debounce and request
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
      autoSearchTimerRef.current = null;
    }
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
    }
    if (vehicleTypeaheadTimerRef.current) {
      clearTimeout(vehicleTypeaheadTimerRef.current);
    }
    if (value.length >= 2) {
      vehicleTypeaheadTimerRef.current = setTimeout(() => {
        searchVehicleTypeahead(value);
      }, 500);
    } else {
      setVehicleTypeaheadOptions([]);
    }

    // Debounced auto-search for existing vehicles (free, no quota impact)
    if (value.length >= 3) {
      lookupAbortControllerRef.current = new AbortController();
      autoSearchTimerRef.current = setTimeout(async () => {
        const result = await searchExistingVehicle(value);
        if (result) {
          applyFoundVehicle(result, value);
        }
      }, 500);
    }
  }, [searchVehicleTypeahead, searchExistingVehicle, applyFoundVehicle]);

  /** A typeahead option was selected: free by-registration fetch only. */
  const selectTypeaheadOption = useCallback(async (option) => {
    setVehicleTypeaheadOptions([]);
    if (vehicleTypeaheadTimerRef.current) {
      clearTimeout(vehicleTypeaheadTimerRef.current);
    }
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
    }
    setLookupError(null);
    setShowNonFinnishInfo(false);
    setShowTrialInfo(false);
    const result = await searchExistingVehicle(option.registration_number);
    if (result) {
      applyFoundVehicle(result, option.registration_number);
    }
  }, [searchExistingVehicle, applyFoundVehicle]);

  // Confirmed extra lookup (over-quota)
  const confirmExtraLookup = useCallback(async (regNumber) => {
    const reg = regNumber || pendingRegNumber;
    if (!reg) return;

    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
    }
    lookupAbortControllerRef.current = new AbortController();

    setLookupLoading(true);
    setLookupError(null);

    try {
      const response = await axios.post('/api/vehicles/lookup/', {
        registration_number: reg.toUpperCase(),
        confirm_extra: true,
      }, {
        signal: lookupAbortControllerRef.current.signal,
      });

      if (response.data.success && response.data.vehicle) {
        callbacksRef.current.onVehicleData?.(response.data.vehicle, reg);
        fetchQuota();
      } else {
        setLookupError(response.data.error || t('Vehicle not found'));
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }
      console.error('Vehicle lookup with confirm failed:', error);
      setLookupError(t('Vehicle lookup failed'));
    } finally {
      setLookupLoading(false);
    }
  }, [pendingRegNumber, t, fetchQuota]);

  /**
   * Explicit lookup (button click / Enter). Org-DB first (free); a hit
   * short-circuits. Non-Finnish plates get an info banner instead of the
   * paid lookup.
   */
  const requestLookup = useCallback(async (regNumber) => {
    if (!regNumber) return;

    // Cancel any pending auto-search debounce
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
      autoSearchTimerRef.current = null;
    }

    // Cancel any previous lookup request
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
    }
    lookupAbortControllerRef.current = new AbortController();

    setLookupLoading(true);
    setLookupError(null);
    setShowTrialInfo(false);
    setShowNonFinnishInfo(false);

    try {
      // First check if vehicle exists in org database (free)
      const result = await searchExistingVehicle(regNumber);
      if (result) {
        applyFoundVehicle(result, regNumber);
        setLookupLoading(false);
        return;
      }

      // Not Finnish pattern - show info
      if (!isFinnishRegistrationNumber(regNumber)) {
        setShowNonFinnishInfo(true);
        setLookupLoading(false);
        return;
      }

      // Finnish pattern - try external lookup
      const response = await axios.post('/api/vehicles/lookup/', {
        registration_number: regNumber.toUpperCase(),
      }, {
        signal: lookupAbortControllerRef.current.signal,
      });

      if (response.data.success && response.data.vehicle) {
        callbacksRef.current.onVehicleData?.(response.data.vehicle, regNumber);
        fetchQuota();
      } else {
        setLookupError(response.data.error || t('Vehicle not found'));
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError' || error.name === 'CanceledError') {
        return;
      }

      if (error.response?.status === 402) {
        const data = error.response.data;
        if (data.quota_info) {
          setQuotaInfo(data.quota_info);
        }
        if (data.trial_restricted) {
          setShowTrialInfo(true);
          callbacksRef.current.onTrialRestricted?.();
        } else if (data.requires_confirmation) {
          setPendingRegNumber(regNumber);
          // Auto-confirm if user opted to skip dialog
          if (shouldSkipExtraLookupConfirm()) {
            confirmExtraLookup(regNumber);
          } else {
            setExtraLookupDialogOpen(true);
          }
        }
        setLookupLoading(false);
        return;
      }

      if (error.response?.status === 429) {
        setLookupError(t('vehicleLookup.rateLimited'));
      } else if (error.response?.status === 503) {
        setLookupError(t('vehicleLookup.serviceError'));
      } else {
        console.error('Vehicle lookup failed:', error);
        setLookupError(t('Vehicle lookup failed'));
      }
    } finally {
      setLookupLoading(false);
    }
  }, [t, searchExistingVehicle, applyFoundVehicle, fetchQuota, confirmExtraLookup]);

  /** Interrupt an in-flight lookup (e.g. "Enter manually" while loading). */
  const interruptLookup = useCallback(() => {
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
      lookupAbortControllerRef.current = null;
    }
    setLookupLoading(false);
    setLookupError(null);
  }, []);

  /** Cancel any in-flight lookup without touching errors (used on submit). */
  const cancelLookup = useCallback(() => {
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
      lookupAbortControllerRef.current = null;
    }
    setLookupLoading(false);
  }, []);

  /** Full reset for dialog close. */
  const resetLookup = useCallback(() => {
    setLookupLoading(false);
    setLookupError(null);
    setVehicleLookedUp(false);
    setShowTrialInfo(false);
    setShowNonFinnishInfo(false);
    setExtraLookupDialogOpen(false);
    setPendingRegNumber(null);
    if (lookupAbortControllerRef.current) {
      lookupAbortControllerRef.current.abort();
    }
    if (autoSearchTimerRef.current) {
      clearTimeout(autoSearchTimerRef.current);
    }
    if (vehicleTypeaheadTimerRef.current) {
      clearTimeout(vehicleTypeaheadTimerRef.current);
    }
    setVehicleTypeaheadOptions([]);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSearchTimerRef.current) {
        clearTimeout(autoSearchTimerRef.current);
      }
      if (lookupAbortControllerRef.current) {
        lookupAbortControllerRef.current.abort();
      }
      if (vehicleTypeaheadTimerRef.current) {
        clearTimeout(vehicleTypeaheadTimerRef.current);
      }
    };
  }, []);

  return {
    lookupLoading,
    lookupError,
    vehicleLookedUp,
    setVehicleLookedUp,
    showTrialInfo,
    showNonFinnishInfo,
    quotaInfo,
    fetchQuota,
    extraLookupDialogOpen,
    setExtraLookupDialogOpen,
    pendingRegNumber,
    vehicleTypeaheadOptions,
    vehicleTypeaheadLoading,
    vehicleTypeaheadOpenRef,
    handleRegistrationInput,
    selectTypeaheadOption,
    requestLookup,
    confirmExtraLookup,
    interruptLookup,
    cancelLookup,
    resetLookup,
  };
}
